import { ApplicationRef, DestroyRef, inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, interval } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private appRef = inject(ApplicationRef);
  private swUpdate = inject(SwUpdate);
  private destroyRef = inject(DestroyRef);
  private snackBar = inject(MatSnackBar);

  public init(): void {
    if (!this.swUpdate.isEnabled) {
      console.log('Service Worker is not enabled');
      return;
    }

    // Check for updates when the app is stable
    const appIsStable$ = this.appRef.isStable.pipe(
      first(isStable => isStable === true)
    );

    // Check for updates every 6 hours after the app is stable
    const everySixHours$ = interval(6 * 60 * 60 * 1000);
    const everySixHoursOnceAppIsStable$ = concat(appIsStable$, everySixHours$);

    everySixHoursOnceAppIsStable$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.swUpdate.checkForUpdate()
          .then(() => {
            console.log('Checked for updates');
          })
          .catch((err) => {
            console.error('Failed to check for updates:', err);
          });
      });

    // Listen for version updates
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(evt => {
        console.log('New version available:', evt.latestVersion);
      });
  }

  public getUpdateAvailable$() {
    return this.swUpdate.versionUpdates.pipe(
      filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
    );
  }

  public checkForUpdate(): void {
    if (!this.swUpdate.isEnabled) {
      console.log('Service Worker is not enabled - cannot check for updates');
      this.snackBar.open('Service Worker är inte aktiverad', 'Stäng', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
      return;
    }
    
    this.snackBar.open('Söker efter uppdateringar...', undefined, {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
    
    this.swUpdate.checkForUpdate()
      .then((updateFound) => {
        if (updateFound) {
          console.log('Update found!');
          this.snackBar.open('Uppdatering hittad! Uppdatera sidan för att installera.', 'Stäng', {
            duration: 30000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom'
          });
        } else {
          console.log('Already on the latest version');
          this.snackBar.open('Du har redan den senaste versionen', 'Stäng', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom'
          });
        }
      })
      .catch((err) => {
        console.error('Failed to check for updates:', err);
        this.snackBar.open('Kunde inte söka efter uppdateringar', 'Stäng', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        });
      });
  }

  public activateUpdate(): void {
    this.swUpdate.activateUpdate()
      .then(() => {
        console.log('Update activated, reloading...');
        document.location.reload();
      })
      .catch((err) => {
        console.error('Failed to activate update:', err);
      });
  }
}
